/**
 * Sequences mount/unmount of a per-page MFE across SPA navigations. Without this,
 * an outgoing and incoming instance can briefly coexist and corrupt shared
 * DOM/global state (e.g. a fixed-id portal node) during the handoff.
 */

type PendingHandoff = {
  acknowledged: boolean;
  instanceId: string;
};

const pendingByModule = new Map<string, PendingHandoff>();
const listenersByModule = new Map<string, Set<() => void>>();

function notifyListeners(moduleName: string) {
  listenersByModule.get(moduleName)?.forEach(listener => listener());
}

export function claimMount(moduleName: string, instanceId: string): boolean {
  const pending = pendingByModule.get(moduleName);

  if (!pending || pending.acknowledged) {
    pendingByModule.set(moduleName, { acknowledged: false, instanceId });

    return true;
  }

  return false;
}

// The "give up waiting" escape hatch — notifies subscribers so a superseded
// instance can demote itself instead of staying mounted alongside the new claimant.
export function forceClaimMount(moduleName: string, instanceId: string) {
  pendingByModule.set(moduleName, { acknowledged: false, instanceId });
  notifyListeners(moduleName);
}

export function acknowledgeTeardown(moduleName: string, instanceId: string) {
  const pending = pendingByModule.get(moduleName);
  if (pending?.instanceId !== instanceId) return;

  pending.acknowledged = true;
  notifyListeners(moduleName);
}

export function isCurrentClaimant(
  moduleName: string,
  instanceId: string
): boolean {
  return pendingByModule.get(moduleName)?.instanceId === instanceId;
}

// Fires on both acknowledgeTeardown and forceClaimMount — treat every
// notification as "re-check ownership," not only "a slot has freed up."
export function onTeardownAcknowledged(
  moduleName: string,
  listener: () => void
) {
  if (!listenersByModule.has(moduleName)) {
    listenersByModule.set(moduleName, new Set());
  }
  listenersByModule.get(moduleName)!.add(listener);

  return () => listenersByModule.get(moduleName)?.delete(listener);
}

export function resetMfeMountGate(moduleName: string) {
  pendingByModule.delete(moduleName);
  listenersByModule.delete(moduleName);
}
